import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { ButtonIcons } from 'src/app/components/action-button/models/button-icons.enum';
import { JobDigitalLogFormRequest } from 'src/app/models/digital-log/digital-log.model';
import { ActionsPlaceholderSettings } from 'src/app/models/enum/actions-placeholder-settings.enum';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { SettingsPreferences } from 'src/app/services/ui/models/settings-preferences.model';

import * as moment from 'moment';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomizeModel, MonthConfig, WeekFormat } from '@vitralogy/ng-calendar';
import { JobDigitalLogOfflineRange } from '@vitralogy/ng-digital-log';
import { DigitalLogService } from 'src/app/services/sections/job/digital-log.service';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LegendBullet } from 'src/app/components/legend/models/legend-bullet.model';
import { NavigationActionButton } from 'src/app/components/navigation-bar/models/navigation-action-button.model';
import { DigitalLogHelper } from '../helpers/digital-log.helper';

@Component({
	templateUrl: './digital-log-form-calendar.component.html',
	styleUrls: ['./digital-log-form-calendar.component.scss', '../../../stylesheets/job-main.scss', '../../../stylesheets/job-actions.scss', '../../../stylesheets/job-inputs.scss', '../../../stylesheets/job-form.scss']
})
export class DigitalLogFormCalendarComponent implements OnInit, OnDestroy {
	public formRequests: JobDigitalLogFormRequest[];
	public customerPreferences: SettingsPreferences;

	public readonly ActionsPlaceholderSettings = ActionsPlaceholderSettings;
	public readonly ButtonIcons = ButtonIcons;
	public readonly moment = moment;
	public readonly visibleYears = this.getVisibleYears();
	public readonly legendBullets = this.getLegendBullets();

	public selectedDate = null;
	public isLoadingTimeline = false;

	public config: MonthConfig = {
		customization: [],
		weekFormat: WeekFormat.Gregorian,
		disableDefaultClick: true,
		showCustomizationOnDaysNotInMonth: false
	};

	public filtersFormGroup: FormGroup;

	private cancelRequest$: Subject<void> = new Subject<void>();

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private digitalLogService: DigitalLogService
	) { }

	ngOnInit(): void {
		this.formRequests = this.activatedRoute.snapshot.data.formRequests;
		this.customerPreferences = this.activatedRoute.snapshot.data.customerPreferences as SettingsPreferences;
		// let selectedFormByFrequencyTypeId = this.activatedRoute.snapshot.queryParamMap.get('rf');
		GlobalSettingsService.priorityNavigationBarActions = new Map<string, NavigationActionButton>();
		GlobalSettingsService.priorityNavigationBarActions.set('close_page', {
			action: () => this.router.navigate(['../'], { relativeTo: this.activatedRoute }),
			icon: 'close',
			listOrder: 1
		});

		this.filtersFormGroup = new FormGroup({
			formRequest: new FormControl(this.formRequests ? this.formRequests[0] : null, [Validators.required]),
			year: new FormControl(null, [Validators.required])
		});

		this.filtersFormGroup.get('year').valueChanges
			.subscribe(year => {
				const date = moment(year.toString(), 'YYYY').startOf('year');
				this.selectedDate = date;
				this.refreshFormRequest(this.filtersFormGroup.get('formRequest').value.jobDigitalLogFormRequestId, year);
			});

		this.filtersFormGroup.get('formRequest').valueChanges
			.subscribe(formRequest => {
				this.refreshFormRequest(formRequest.jobDigitalLogFormRequestId, this.filtersFormGroup.get('year').value);
			});

		this.filtersFormGroup.get('year').setValue(moment().year());

		this.populatePreselectionFromQuery();

		GlobalSettingsService.isSecondaryLoaderLoading = false;
		GlobalSettingsService.isGlobalLoaderLoading = false;
	}

	ngOnDestroy(): void {
		GlobalSettingsService.priorityNavigationBarActions = null;
	}

	private populatePreselectionFromQuery(): void {
		const preselectedFormRequestId = this.activatedRoute.snapshot.queryParamMap.get('r');
		const preselectedYear = this.activatedRoute.snapshot.queryParamMap.get('y');
		const preselectedRecurrenceFrequencyId = this.activatedRoute.snapshot.queryParamMap.get('rf');

		if (preselectedRecurrenceFrequencyId != null) {
			let selectedRequest = null;

			if (preselectedRecurrenceFrequencyId === '') {
				selectedRequest = (this.formRequests.find(item => !item.recurrenceFrequencyId) || this.formRequests[0]);
			} else {
				const frequencyIdNumber = parseInt(preselectedRecurrenceFrequencyId, 10);

				if (!isNaN(frequencyIdNumber)) {
					selectedRequest = (this.formRequests.find(item => item.recurrenceFrequencyId === frequencyIdNumber) || this.formRequests[0]);
				}
			}

			this.filtersFormGroup.get('formRequest').setValue(selectedRequest);
		}

		if (!!preselectedFormRequestId) {
			const formRequestIdNumber = parseInt(preselectedFormRequestId, 10);
			const request = this.formRequests?.find(item => item.jobDigitalLogFormRequestId === formRequestIdNumber);

			if (!!request) {
				this.filtersFormGroup.get('formRequest').setValue(request);
			}
		}

		if (!!preselectedYear) {
			this.filtersFormGroup.get('year').setValue(parseInt(preselectedYear, 10) || moment().year());
		}
	}

	private refreshFormRequest(jobDigitalLogFormRequestId: number, year: number): void {
		this.isLoadingTimeline = true;
		this.cancelRequest$.next();

		this.router.navigate([], {
			relativeTo: this.activatedRoute,
			queryParams: {
				r: this.filtersFormGroup.get('formRequest').value.jobDigitalLogFormRequestId,
				y: this.filtersFormGroup.get('year').value
			}
		});

		forkJoin([
			this.digitalLogService.getFormCompletionTimeline(TokenHelper.getToken(this.activatedRoute), jobDigitalLogFormRequestId, year),
			this.digitalLogService.getOfflineRanges(TokenHelper.getToken(this.activatedRoute), jobDigitalLogFormRequestId)
		])
			.pipe(takeUntil(this.cancelRequest$))
			.subscribe(([timeline, offlineRanges]: [{ timeSlots: any[], year: number }, JobDigitalLogOfflineRange[]]) => {
				const formRequest = this.filtersFormGroup.get('formRequest').value;
				this.config.customization = [];

				timeline.timeSlots.forEach(item => {
					if (!DigitalLogHelper.isWithinOfflineRange(item.timeSlotDate, offlineRanges)) {
						this.config.customization.push({
							date: moment(item.timeSlotDate).format('MM/DD/YYYY'),
							color: item.sampleCssColor,
							border: item.cssBorderColor,
							customCallback: (() => {
								this.router.navigate(
									['../' + formRequest.jobDigitalLogFormRequestId + '/form'],
									{ relativeTo: this.activatedRoute, queryParams: { customDate: moment(item.timeSlotDate).format('MM/DD/YYYY') } }
								);
							}).bind(this)
						});
					}
				});

				this.config = { ...this.config };
				this.isLoadingTimeline = false;
			});
	}

	private getVisibleYears(): number[] {
		const currentYear = moment().year();
		return [-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(offset => currentYear + offset);
	}

	private getLegendBullets(): LegendBullet[] {
		return [{
			color: '#ff9494',
			text: 'Missing'
		}, {
			color: '#f3f378',
			text: 'Incomplete'
		}, {
			color: '#8cc58c',
			text: 'Complete'
		}, {
			color: '#bbbbbb',
			text: 'Upcoming'
		}, {
			color: '#f7f7f7',
			text: 'Date Changed',
			border: '#909090'
		}];
	}
}
